import { isAssistantAccountType } from "@/constants/accountTypes";
import { useAppSelector } from "@/redux/hooks";
import { profileSelector } from "@/redux/slices/profile/selectors";

export interface ClientNavLink {
  name: string;
  to: string;
  icon: string;
}

export const clientNavLinks: ClientNavLink[] = [
  { name: "Home", to: "/client", icon: "Home2" },
  { name: "My Favourites", to: "/client/favourites", icon: "Heart" },
  { name: "Settings", to: "/client/settings", icon: "Setting2" },
];

// "My Clients" and "My Profile" are assistant-only — they sit between the landing and My Favourites
// for accounts that manage other clients (Executive Assistant, Travel Manager). For assistants the
// landing (/client) is relabelled "All Bookings" (merged across managed clients); "My Profile"
// (DEV-405) shows the assistant's own details + their own booking history.
const myClientsLink: ClientNavLink = { name: "My Clients", to: "/client/my-clients", icon: "Profile2User" };
const myProfileLink: ClientNavLink = { name: "My Profile", to: "/client/my-profile", icon: "Profile" };

/**
 * True when the logged-in account can manage other clients — i.e. an Executive Assistant or a
 * Travel Manager. Gates the "My Clients" experience.
 */
export const useCanManageAsAssistant = (): boolean => {
  const profile = useAppSelector(profileSelector);
  return isAssistantAccountType(profile?.accountType);
};

/** Nav links for the client portal, injecting the assistant-only "My Clients" entry when applicable. */
export const useClientNavLinks = (): ClientNavLink[] => {
  const canManage = useCanManageAsAssistant();
  if (!canManage) return clientNavLinks;
  const [home, ...rest] = clientNavLinks;
  // For assistants the landing lists all managed clients' bookings, so it reads "All Bookings".
  const allBookings: ClientNavLink = { ...home, name: "All Bookings" };
  return [allBookings, myClientsLink, myProfileLink, ...rest];
};
